import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeftRight, ChevronDown, Loader2, Eye, FileDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AccountReportView, CompareHeadToHead } from '@/components/ProductMixAccountReport';
import { API, errorText } from '@/utils/api';

// Compare two regions, stores or customers over the Product Mix period:
// dispatched composition by quality, fabric and price band, side by side,
// with an overlap score for each (Σ of the smaller share — 100% = the two
// buy in identical proportions, 0% = nothing in common).

const KINDS = [
  { key: 'region', label: 'Regions', one: 'Region' },
  { key: 'store', label: 'Stores', one: 'Store' },
  { key: 'customer', label: 'Customers', one: 'Customer' },
];
const A_COLOR = '#4F46E5';
const B_COLOR = '#D97706';
const FABRIC_COLORS = { Shirting: '#3B82F6', Suiting: '#8B5CF6', Ethnic: '#EC4899', Other: '#14B8A6', Unclear: '#CBD5E1' };

const fmtInr = (v) => `₹${Math.round(v || 0).toLocaleString('en-IN')}`;
const fmtNum = (v) => Math.round(v || 0).toLocaleString('en-IN');
const pct = (v) => `${(v || 0).toFixed(1)}%`;
// Price bands in rate order ("≤ ₹150", "₹151–300", … "₹2,101+", "Unpriced").
const bandStart = (label) => {
  if (label.startsWith('≤')) return 0;
  const m = label.replace(/,/g, '').match(/\d+/);
  return m ? parseInt(m[0], 10) : 1e9;
};

export function SidePicker({ options, value, onChange, placeholder, color }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const picked = options.find(o => o.key === value);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? options.filter(o => `${o.label} ${o.hint || ''}`.toLowerCase().includes(s)) : options;
    return list.slice(0, 200);
  }, [options, q]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          className="h-9 min-w-[220px] max-w-[320px] px-3 rounded-md border bg-white text-sm text-left flex items-center gap-2 hover:bg-gray-50"
          style={{ borderColor: color }}>
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
          <span className={`truncate flex-1 ${picked ? 'text-gray-900' : 'text-gray-400'}`}>
            {picked ? picked.label : placeholder}
          </span>
          <ChevronDown size={14} className="text-gray-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-2" align="start">
        <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="h-8 text-sm mb-2" />
        <div className="max-h-72 overflow-y-auto">
          {shown.length === 0 && <p className="text-xs text-gray-500 px-2 py-3">Nothing matches.</p>}
          {shown.map(o => (
            <button key={o.key} type="button" onClick={() => { onChange(o.key); setOpen(false); setQ(''); }}
              className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 ${o.key === value ? 'bg-gray-100 font-medium' : ''}`}>
              <div className="truncate">{o.label}</div>
              {o.hint && <div className="text-[11px] text-gray-500 truncate">{o.hint}</div>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ShareBars({ a, b, max }) {
  const w = (v) => `${Math.max(0, Math.min(100, (v / (max || 1)) * 100))}%`;
  return (
    <div className="w-40 space-y-0.5">
      <div className="h-1.5 rounded-full" style={{ width: w(a), background: A_COLOR }} />
      <div className="h-1.5 rounded-full" style={{ width: w(b), background: B_COLOR }} />
    </div>
  );
}

const FABRIC_ORDER = ['Shirting', 'Suiting', 'Ethnic', 'Other', 'Unclear', 'Unclassified'];
const fmtRate = (v) => `₹${Math.round(v).toLocaleString('en-IN')}`;

// Ex-mill ₹/m next to a quality: one figure when both sides bought at the
// same rate, A and B separately when they differ by 1% or more. From the
// dispatched SO rate (metres-weighted); * = only ordered in the period.
function ExMill({ name, exA, exB }) {
  const a = exA[name], b = exB[name];
  if (!a && !b) return null;
  const tip = (x) => (x?.source === 'order' ? ' (ordered rate — not dispatched in the period)' : '');
  if (a && b && Math.abs(a.rate - b.rate) / Math.max(a.rate, b.rate) >= 0.01) {
    return (
      <span className="ml-1.5 text-[10px] font-normal tabular-nums whitespace-nowrap" title={`Ex-mill ₹/m — A${tip(a)}, B${tip(b)}`}>
        <span style={{ color: A_COLOR }}>A {fmtRate(a.rate)}{a.source === 'order' ? '*' : ''}</span>
        <span className="text-gray-400"> · </span>
        <span style={{ color: B_COLOR }}>B {fmtRate(b.rate)}{b.source === 'order' ? '*' : ''}</span>
      </span>
    );
  }
  const x = a || b;
  return (
    <span className="ml-1.5 text-[10px] font-normal text-gray-500 tabular-nums whitespace-nowrap" title={`Ex-mill ₹/m${tip(x)}`}>
      {fmtRate(x.rate)}/m{x.source === 'order' ? '*' : ''}
    </span>
  );
}

// Metres-weighted ex-mill of one side's dispatched qualities in a fabric.
function sectionExMill(side, fabricOf, fabric) {
  let rm = 0; let m = 0;
  for (const t of side.trades || []) {
    if ((fabricOf[t.trade_name] || 'Unclassified') !== fabric) continue;
    const e = (side.ex_mill || {})[t.trade_name];
    const metres = t.metres || t.qty || 0;
    if (e && e.source === 'dispatch' && metres > 0) { rm += e.rate * metres; m += metres; }
  }
  return m > 0 ? rm / m : null;
}

function Highlights({ rows, aLabel, bLabel, fabricOf, exA, exB }) {
  const both = rows.filter(r => r.in === 'both');
  const topShared = [...both].sort((x, y) => (y.a + y.b) - (x.a + x.b)).slice(0, 6);
  const aHeavy = rows.filter(r => r.diff >= 3).sort((x, y) => y.diff - x.diff).slice(0, 6);
  const bHeavy = rows.filter(r => r.diff <= -3).sort((x, y) => x.diff - y.diff).slice(0, 6);
  const onlyA = rows.filter(r => r.in === 'a' && r.a >= 1).slice(0, 6);
  const onlyB = rows.filter(r => r.in === 'b' && r.b >= 1).slice(0, 6);
  const List = ({ title, items, render, tone }) => (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className={`text-[11px] font-semibold mb-1.5 ${tone}`}>{title}</div>
      {items.length === 0 ? <div className="text-[11px] text-gray-400">None</div> : (
        <ul className="space-y-0.5">{items.map(r => (
          <li key={r.name} className="text-xs flex justify-between gap-2">
            <span className="truncate">
              {r.name}
              {fabricOf[r.name] && <span className="ml-1 text-[10px] text-gray-400">{fabricOf[r.name]}</span>}
              <ExMill name={r.name} exA={exA} exB={exB} />
            </span>
            <span className="tabular-nums text-gray-600 shrink-0">{render(r)}</span>
          </li>
        ))}</ul>
      )}
    </div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      <List title="Both buy heavily" tone="text-emerald-700" items={topShared} render={r => `${pct(r.a)} · ${pct(r.b)}`} />
      <List title={`Much more in ${aLabel} (A)`} tone="text-indigo-700" items={aHeavy} render={r => `+${r.diff.toFixed(1)} pp`} />
      <List title={`Much more in ${bLabel} (B)`} tone="text-amber-700" items={bHeavy} render={r => `+${Math.abs(r.diff).toFixed(1)} pp`} />
      <List title={`Only ${aLabel} (A) buys`} tone="text-indigo-700" items={onlyA} render={r => pct(r.a)} />
      <List title={`Only ${bLabel} (B) buys`} tone="text-amber-700" items={onlyB} render={r => pct(r.b)} />
    </div>
  );
}

// Store / customer lists for a division (loaded once, when first needed) —
// shared by Compare and the Product Mix account picker.
export function useAccountOptions(selectedDivision, needed) {
  const [opts, setOpts] = useState({ stores: [], customers: [] });
  const [loadedFor, setLoadedFor] = useState(null);
  useEffect(() => {
    if (!needed || loadedFor === selectedDivision) return;
    let live = true;
    axios.get(`${API}/analytics/region-product-mix/compare-options`, {
      params: { division: selectedDivision !== 'all' ? selectedDivision : undefined }, withCredentials: true,
    }).then(r => { if (live) { setOpts(r.data || { stores: [], customers: [] }); setLoadedFor(selectedDivision); } })
      .catch(e => toast.error(errorText(e) || 'Could not load stores and customers'));
    return () => { live = false; };
  }, [needed, selectedDivision, loadedFor]);
  return loadedFor === selectedDivision ? opts : { stores: [], customers: [] };
}

export const accountOptions = (kind, opts, regionNames = []) => {
  if (kind === 'region') return regionNames.map(r => ({ key: r, label: r }));
  if (kind === 'store') return (opts.stores || []).map(s => ({ key: s.key, label: s.customer ? `${s.customer} — ${s.label}` : s.label, hint: `Store ${s.code} · ${s.division}` }));
  return (opts.customers || []).map(c => ({ key: c.key, label: c.label, hint: c.location }));
};

export function KindSwitch({ kinds = KINDS, value, onChange }) {
  return (
    <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
      {kinds.map(k => (
        <button key={k.key} type="button" onClick={() => onChange(k.key)}
          className={`text-[11px] px-2 py-1 rounded-md transition font-medium ${value === k.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
          {k.one}
        </button>
      ))}
    </div>
  );
}

export default function ProductMixCompare({ selectedDivision, periodType, periodValue, periodLabel, metric, regionNames, topChoice }) {
  // Each side picks its own kind, so a store can be read against its region.
  const [kindA, setKindA] = useState('region');
  const [kindB, setKindB] = useState('region');
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const opts = useAccountOptions(selectedDivision, kindA !== 'region' || kindB !== 'region');

  useEffect(() => { setA(''); setB(''); setResult(null); }, [selectedDivision]);
  useEffect(() => { setResult(null); }, [periodType, periodValue]);

  const optionsA = useMemo(() => accountOptions(kindA, opts, regionNames || []), [kindA, opts, regionNames]);
  const optionsB = useMemo(() => accountOptions(kindB, opts, regionNames || []), [kindB, opts, regionNames]);

  const compareParams = () => ({
    kind: kindA, a_kind: kindA, b_kind: kindB, a, b,
    division: selectedDivision !== 'all' ? selectedDivision : undefined,
    period_type: periodType, period_value: periodValue,
  });

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await axios.get(`${API}/analytics/region-product-mix/compare/pdf`, {
        params: compareParams(), responseType: 'blob', withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const fname = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'ProductMix_Compare.pdf';
      const el = document.createElement('a');
      el.href = url; el.download = fname;
      document.body.appendChild(el); el.click(); el.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setPdfBusy(false);
    }
  };

  const run = async () => {
    if (!a || !b) { toast.error('Pick both sides'); return; }
    if (kindA === kindB && a === b) { toast.error('Pick two different sides'); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/region-product-mix/compare`, {
        params: compareParams(),
        withCredentials: true,
      });
      setResult(res.data);
      setShowAll(false);
    } catch (e) {
      toast.error(errorText(e) || 'Compare failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const cmp = result?.compare?.[metric] || null;
  const qRows = cmp?.qualities?.rows || [];
  const limit = topChoice === 'all' ? qRows.length : (parseInt(topChoice, 10) || 20);
  const fabricOf = result?.compare?.fabric_of || {};
  const exA = result?.a?.ex_mill || {};
  const exB = result?.b?.ex_mill || {};
  // Qualities grouped by fabric; shares re-based to the fabric so A and B are
  // compared like for like inside Shirting, Suiting, Ethnic…
  const sections = FABRIC_ORDER.map(fab => {
    const rows = qRows.filter(r => (fabricOf[r.name] || 'Unclassified') === fab);
    const totA = rows.reduce((t, r) => t + r.a, 0);
    const totB = rows.reduce((t, r) => t + r.b, 0);
    const inRows = rows.map(r => {
      const ia = totA ? (r.a / totA) * 100 : 0;
      const ib = totB ? (r.b / totB) * 100 : 0;
      return { ...r, ia, ib, igap: ia - ib };
    }).sort((x, y) => Math.max(y.ia, y.ib) - Math.max(x.ia, x.ib));
    return {
      fabric: fab, rows: inRows, totA, totB,
      exA: result ? sectionExMill(result.a, fabricOf, fab) : null,
      exB: result ? sectionExMill(result.b, fabricOf, fab) : null,
      maxShare: Math.max(1, ...inRows.slice(0, 50).map(r => Math.max(r.ia, r.ib))),
    };
  }).filter(sec => sec.rows.length > 0);
  const hiddenCount = showAll ? 0 : sections.reduce((t, sec) => t + Math.max(0, sec.rows.length - limit), 0);
  const unit = metric === 'value' ? '₹' : 'metres';
  const bands = cmp?.bands || {};
  const emptySide = result && (!(result.a.dispatches?.total_net_value || result.a.dispatches?.total_metres)
    || !(result.b.dispatches?.total_net_value || result.b.dispatches?.total_metres));
  const kindWord = (k) => KINDS.find(x => x.key === k)?.one.toLowerCase() || k;

  return (
    <div className="space-y-4">
      <Card className="border border-gray-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <KindSwitch value={kindA} onChange={(k) => { setKindA(k); setA(''); setResult(null); }} />
            <SidePicker options={optionsA} value={a} onChange={setA} color={A_COLOR}
              placeholder={`A — pick a ${kindWord(kindA)}`} />
          </div>
          <button type="button" title="Swap" onClick={() => { setA(b); setB(a); setKindA(kindB); setKindB(kindA); setResult(null); }}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><ArrowLeftRight size={14} /></button>
          <div className="flex items-center gap-2">
            <KindSwitch value={kindB} onChange={(k) => { setKindB(k); setB(''); setResult(null); }} />
            <SidePicker options={optionsB} value={b} onChange={setB} color={B_COLOR}
              placeholder={`B — pick a ${kindWord(kindB)}`} />
          </div>
          <Button size="sm" onClick={run} disabled={loading || !a || !b} className="h-9 text-xs gap-1.5">
            {loading && <Loader2 size={14} className="animate-spin" />} Compare
          </Button>
          <span className="text-xs text-gray-500 ml-auto">{periodLabel} · dispatched mix, net of returns</span>
        </CardContent>
      </Card>

      {!result && !loading && (
        <p className="text-xs text-gray-500 px-1">
          Pick what to compare on each side — a region, a store or a customer (they can differ: e.g. a store against its region) — and press Compare to see their purchase composition side by side for {periodLabel}.
          The period and the ₹ / metres switch above apply here too.
        </p>
      )}

      {result && cmp && (
        <>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setFullOpen(true)} disabled={!result.reports} className="h-9 text-xs gap-1.5">
              <Eye size={14} /> View full reports
            </Button>
            <Button size="sm" onClick={downloadPdf} disabled={pdfBusy} className="h-9 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700">
              {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Compare PDF
            </Button>
          </div>

          {emptySide && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              One side has no dispatches in {periodLabel}, so there is nothing to compare its mix against. Try a wider period.
            </p>
          )}

          {result.reports && <CompareHeadToHead res={result} metric={metric} />}

          <Highlights rows={qRows} aLabel={result.a.label} bLabel={result.b.label} fabricOf={fabricOf} exA={exA} exB={exB} />

          <Card className="border border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Qualities side by side, by fabric — share of {unit} within each fabric</CardTitle>
              <p className="text-[11px] text-gray-500">
                Ex-mill ₹/m next to each quality is the metres-weighted SO rate it was dispatched at; A and B are shown separately when their rates differ.
                * = only ordered in the period, so the ordered ex-mill rate is shown.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Quality · ex-mill ₹/m</th>
                      <th className="text-right px-3 py-2 font-medium" style={{ color: A_COLOR }}>A %</th>
                      <th className="text-right px-3 py-2 font-medium" style={{ color: B_COLOR }}>B %</th>
                      <th className="px-3 py-2"></th>
                      <th className="text-right px-3 py-2 font-medium">Gap (A − B)</th>
                      <th className="text-left px-3 py-2 font-medium">Bought by</th>
                    </tr>
                  </thead>
                  {sections.map(sec => (
                    <tbody key={sec.fabric}>
                      <tr className="border-t-2 border-gray-200" style={{ background: `${FABRIC_COLORS[sec.fabric] || '#94A3B8'}14` }}>
                        <td className="px-3 py-2" colSpan={6}>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span className="flex items-center gap-1.5 font-semibold text-gray-900">
                              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: FABRIC_COLORS[sec.fabric] || '#94A3B8' }} />
                              {sec.fabric}
                              <span className="font-normal text-gray-500">({sec.rows.length})</span>
                            </span>
                            <span className="text-gray-600">
                              Share of {unit}: <span className="font-semibold tabular-nums" style={{ color: A_COLOR }}>A {pct(sec.totA)}</span>
                              {' · '}<span className="font-semibold tabular-nums" style={{ color: B_COLOR }}>B {pct(sec.totB)}</span>
                            </span>
                            {(sec.exA || sec.exB) && (
                              <span className="text-gray-600" title="Metres-weighted ex-mill (SO) rate of what each side was dispatched in this fabric">
                                Avg ex-mill: <span className="font-semibold tabular-nums" style={{ color: A_COLOR }}>A {sec.exA ? `${fmtRate(sec.exA)}/m` : '—'}</span>
                                {' · '}<span className="font-semibold tabular-nums" style={{ color: B_COLOR }}>B {sec.exB ? `${fmtRate(sec.exB)}/m` : '—'}</span>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {(showAll ? sec.rows : sec.rows.slice(0, limit)).map(r => (
                        <tr key={r.name} className="border-t border-gray-100">
                          <td className="px-3 py-1.5 font-medium text-gray-800">
                            {r.name}<ExMill name={r.name} exA={exA} exB={exB} />
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{r.a ? pct(r.ia) : '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{r.b ? pct(r.ib) : '—'}</td>
                          <td className="px-3 py-1.5"><ShareBars a={r.ia} b={r.ib} max={sec.maxShare} /></td>
                          <td className={`px-3 py-1.5 text-right tabular-nums ${Math.abs(r.igap) >= 3 ? (r.igap > 0 ? 'text-indigo-700 font-semibold' : 'text-amber-700 font-semibold') : 'text-gray-500'}`}>
                            {r.igap > 0 ? '+' : ''}{r.igap.toFixed(1)} pp
                          </td>
                          <td className="px-3 py-1.5 text-gray-600">{r.in === 'both' ? 'Both' : r.in === 'a' ? 'A only' : 'B only'}</td>
                        </tr>
                      ))}
                      {!showAll && sec.rows.length > limit && (
                        <tr className="border-t border-gray-100">
                          <td colSpan={6} className="px-3 py-1 text-[11px] text-gray-400">+ {sec.rows.length - limit} more in {sec.fabric}</td>
                        </tr>
                      )}
                    </tbody>
                  ))}
                </table>
              </div>
              {(hiddenCount > 0 || showAll) && sections.some(sec => sec.rows.length > limit) && (
                <div className="px-3 py-2 border-t border-gray-100 text-xs">
                  <button type="button" className="text-indigo-600 hover:underline" onClick={() => setShowAll(v => !v)}>
                    {showAll ? `Show top ${limit} per fabric` : `Show all ${qRows.length} qualities`}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {Object.keys(bands).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(bands).map(([fab, bc]) => (
                <Card key={fab} className="border border-gray-200">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{fab} — realised ₹/m bands (share of ₹)</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-medium">Band</th>
                          <th className="text-right px-3 py-1.5 font-medium" style={{ color: A_COLOR }}>A %</th>
                          <th className="text-right px-3 py-1.5 font-medium" style={{ color: B_COLOR }}>B %</th>
                          <th className="px-3 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(bc.rows || [])].sort((x, y) => bandStart(x.name) - bandStart(y.name)).map(r => (
                          <tr key={r.name} className="border-t border-gray-100">
                            <td className="px-3 py-1 text-gray-800">{r.name}</td>
                            <td className="px-3 py-1 text-right tabular-nums">{r.a ? pct(r.a) : '—'}</td>
                            <td className="px-3 py-1 text-right tabular-nums">{r.b ? pct(r.b) : '—'}</td>
                            <td className="px-3 py-1"><ShareBars a={r.a} b={r.b} max={Math.max(1, ...(bc.rows || []).map(x => Math.max(x.a, x.b)))} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
      <Dialog open={fullOpen && !!result?.reports} onOpenChange={setFullOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] p-0 flex flex-col gap-0 overflow-hidden">
          {result?.reports && (
            <>
              <DialogHeader className="px-6 pt-4 pb-3 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 pr-8">
                  <DialogTitle className="text-base flex-1 truncate">
                    <span style={{ color: A_COLOR }}>{result.a.label}</span> <span className="text-gray-400">vs</span> <span style={{ color: B_COLOR }}>{result.b.label}</span>
                    <span className="ml-2 text-xs font-normal text-gray-500">{periodLabel}</span>
                  </DialogTitle>
                  {['a', 'b'].map(k => (
                    <button key={k} type="button" onClick={() => document.getElementById(`pm-cmp-${k}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="text-[11px] px-2.5 py-0.5 rounded-full border border-gray-200 hover:bg-indigo-50">{result[k].label}</button>
                  ))}
                  <Button size="sm" onClick={downloadPdf} disabled={pdfBusy} className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700">
                    {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Download PDF
                  </Button>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto bg-slate-50 px-6 py-6 space-y-16">
                <div className="max-w-[1500px] mx-auto"><CompareHeadToHead res={result} metric={metric} /></div>
                <div id="pm-cmp-a" className="scroll-mt-4"><AccountReportView rep={result.reports.a} /></div>
                <div id="pm-cmp-b" className="scroll-mt-4"><AccountReportView rep={result.reports.b} /></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
